import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Angry, Frown, Meh, Smile, Laugh, X } from 'lucide-react';
import { api } from '../services/api';
import './FeedbackModal.css';

/**
 * Feedback — a 1-5 rating, then one question shaped by the answer.
 *
 * The rating comes first and is the only required field: it is the part people will
 * actually complete, and a modal that opens on an empty textarea gets closed. The written
 * question only appears once a face is chosen, so it can be specific about what to write
 * rather than asking for "thoughts".
 *
 * Posts to the existing POST /api/submit-feedback, which takes `topic` ("Feedback" or
 * "Issue") and a `description` of 10-5000 characters. The rating is folded into the
 * description because that endpoint has no field for it — see `composeDescription`.
 */

const FACES = [
  { value: 1, Icon: Angry, label: 'Horrible' },
  { value: 2, Icon: Frown, label: 'Bad' },
  { value: 3, Icon: Meh, label: 'Okay' },
  { value: 4, Icon: Smile, label: 'Good' },
  { value: 5, Icon: Laugh, label: 'Amazing' },
];

/** The written question, chosen by rating — a happy user and an unhappy one need asking different things. */
function followUpFor(rating) {
  if (rating <= 2) return 'Sorry to hear that. What went wrong?';
  if (rating === 3) return 'Thanks. What would make it better?';
  return "Glad you're enjoying AutoInvent! What could make your experience even better?";
}

/**
 * The endpoint stores a plain description, so the rating is written into it rather than
 * lost. Also guarantees the 10-character minimum when someone rates without writing.
 */
function composeDescription(rating, message) {
  const face = FACES.find((f) => f.value === rating);
  const written = message.trim();
  return `Rating: ${rating}/5 (${face ? face.label : 'unknown'})\n\n${written || '(no written comment)'}`;
}

function FeedbackModal({ isOpen, onClose }) {
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // Fresh every time it opens — a modal that reopens showing the last submission looks
  // like the submission failed.
  useEffect(() => {
    if (!isOpen) return;
    setRating(null);
    setMessage('');
    setError(null);
    setDone(false);
    setSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const submit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('topic', 'Feedback');
      body.append('description', composeDescription(rating, message));
      await api.uploadFile('/submit-feedback', body);
      setDone(true);
      setTimeout(onClose, 1800);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      setError(err?.data?.error || 'Could not send your feedback. Please try again.');
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fb-overlay" onClick={onClose} role="presentation">
      <div
        className="fb-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fb-title"
      >
        <button className="fb-close" onClick={onClose} aria-label="Close"><X size={18} /></button>

        {done ? (
          <div className="fb-done">
            <h2 id="fb-title">Thank you</h2>
            <p>Your feedback is on its way to the team.</p>
          </div>
        ) : (
          <>
            <h2 id="fb-title" className="fb-title">Feedback</h2>
            <p className="fb-sub">Share your thoughts and help improve AutoInvent.</p>

            <p className="fb-question">
              How has your experience with AutoInvent been, so far? <span aria-hidden="true">*</span>
            </p>

            <div className="fb-scale-ends">
              <span>Horrible</span>
              <span>Amazing</span>
            </div>

            <div className="fb-faces" role="radiogroup" aria-label="Rate your experience">
              {FACES.map(({ value, Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={rating === value}
                  aria-label={label}
                  title={label}
                  className={`fb-face ${rating === value ? 'is-on' : ''}`}
                  onClick={() => setRating(value)}
                >
                  <Icon size={30} strokeWidth={1.75} />
                </button>
              ))}
            </div>

            {/* Only after a face is picked: before that there is nothing specific to ask. */}
            {rating && (
              <div className="fb-followup">
                <label className="fb-question" htmlFor="fb-message">{followUpFor(rating)}</label>
                <textarea
                  id="fb-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here"
                  rows={5}
                  maxLength={4000}
                />
              </div>
            )}

            {error && <p className="fb-error">{error}</p>}

            <div className="fb-actions">
              <button type="button" className="fb-btn fb-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={submit}
                disabled={!rating || submitting}
              >
                {submitting ? 'Sending…' : 'Submit Feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default FeedbackModal;
